package one.healthium.login.ui.magic_link

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalSoftwareKeyboardController
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import loco.GENERAL_OK
import loco.MAGIC_LINK_EMAIL_PLACEHOLDER
import loco.MAGIC_LINK_INSTRUCTIONS
import loco.MAGIC_LINK_SCREEN_TITLE
import loco.MAGIC_LINK_SEND_BUTTON
import loco.MAGIC_LINK_SUCCESS_MESSAGE
import loco.MAGIC_LINK_SUCCESS_TITLE
import loco.Res
import one.healthium.ui.common.event_producer.EventEffect
import one.healthium.ui.compose.components.AppAlertDialog
import one.healthium.ui.compose.components.AppBackNavigationButton
import one.healthium.ui.compose.components.AppButton
import one.healthium.ui.compose.components.AppLoadingScreen
import one.healthium.ui.compose.components.AppScaffold
import one.healthium.ui.compose.components.AppText
import one.healthium.ui.compose.components.AppTextField
import one.healthium.ui.compose.components.AppTextFieldDefaults
import one.healthium.ui.compose.components.AppTopBar
import one.healthium.ui.compose.components.AppTopBarDefaults
import one.healthium.ui.compose.components.adaptive.WindowSizeConstraintContainer
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.gray_3
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import kotlin.time.Duration.Companion.milliseconds

interface MagicLinkEventDelegate {
    fun onBackClicked()
}

@Composable
fun MagicLinkScreen(email: String?, eventDelegate: MagicLinkEventDelegate) {
    val viewModel = koinViewModel<MagicLinkViewModel>()
    val state by viewModel.state.collectAsState()

    val keyboardController = LocalSoftwareKeyboardController.current
    var showSuccessAlert by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        email?.let {
            viewModel.onUsernameChanged(it)
        }
    }

    EventEffect(viewModel.events) { event ->
        when (event) {
            is MagicLinkUiEvent.OnMagicLinkSent -> {
                keyboardController?.hide()
                showSuccessAlert = true
            }
        }
    }

    MagicLinkScreenContent(
        state = state,
        onBackClicked = {
            keyboardController?.hide()
            eventDelegate.onBackClicked()
        },
        onUsernameChanged = viewModel::onUsernameChanged,
        onSubmitClicked = viewModel::onSubmitClicked,
    )

    if (showSuccessAlert) {
        AppAlertDialog(
            onDismissRequest = {
                showSuccessAlert = false
                eventDelegate.onBackClicked()
            },
            title = stringResource(Res.string.MAGIC_LINK_SUCCESS_TITLE),
            text = stringResource(Res.string.MAGIC_LINK_SUCCESS_MESSAGE),
            positiveButton = stringResource(Res.string.GENERAL_OK),
            onPositiveClick = {
                showSuccessAlert = false
                eventDelegate.onBackClicked()
            }
        )
    }
}

@Composable
private fun MagicLinkScreenContent(
    state: MagicLinkUiState,
    onBackClicked: () -> Unit,
    onUsernameChanged: (String) -> Unit,
    onSubmitClicked: (MagicLinkUiOptions) -> Unit,
) {
    AppScaffold(
        modifier = Modifier.testTag("magic_link_screen"),
        topBar = {
            AppTopBar(
                title = {
                    Text(
                        text = stringResource(Res.string.MAGIC_LINK_SCREEN_TITLE),
                        style = AppTheme.textStyles.headerSemiBold,
                    )
                },
                navigationIcon = { AppBackNavigationButton(onClick = onBackClicked) },
                colors = AppTopBarDefaults.transparent,
            )
        }
    ) {
        // post_mark
        WindowSizeConstraintContainer(Modifier.padding(it)) {
            Column(
                modifier = Modifier
                    .padding(horizontal = 24.dp)
                    .fillMaxSize()
                    .verticalScroll(rememberScrollState()),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.Center,
            ) {
                Spacer(Modifier.weight(1f))
                AppTextField(
                    modifier = Modifier.fillMaxWidth().testTag("magic_link_email"),
                    value = state.magicLinkUiOptions.username,
                    onValueChange = onUsernameChanged,
                    showKeyboardAutomaticallyIfEmpty = true,
                    showKeyboardDelay = 500.milliseconds,
                    colors = AppTextFieldDefaults.colorsGray,
                    shape = AppTextFieldDefaults.shapeRounded,
                    placeholder = { Text(stringResource(Res.string.MAGIC_LINK_EMAIL_PLACEHOLDER)) },
                    maxLines = 1,
                    keyboardOptions = KeyboardOptions(
                        keyboardType = KeyboardType.Email,
                        imeAction = ImeAction.Done,
                    ),
                    keyboardActions = KeyboardActions(
                        onDone = {
                            state.magicLinkUiOptions.let(onSubmitClicked)
                        }
                    ),
                )
                Spacer(Modifier.height(24.dp))

                AppText(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 16.dp),
                    text = stringResource(Res.string.MAGIC_LINK_INSTRUCTIONS),
                    style = AppTheme.textStyles.caption,
                    color = gray_3,
                )
                Spacer(Modifier.height(16.dp))
                Spacer(Modifier.weight(1f))
                val options = state.magicLinkUiOptions
                AppButton(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 16.dp)
                        .testTag("login_button"),
                    enabled = options.isValid,
                    onClick = { options.let(onSubmitClicked) },
                ) {
                    Text(stringResource(Res.string.MAGIC_LINK_SEND_BUTTON))
                }
                Spacer(Modifier.height(25.dp))
                Spacer(Modifier.navigationBarsPadding().imePadding())
            }
        }

        AppLoadingScreen(visible = state.loading, dim = true)
    }
}

@Preview(showBackground = true)
@Composable
fun MagicLinkScreenPreview() {
    AppTheme {
        MagicLinkScreenContent(
            state = MagicLinkUiState(),
            onBackClicked = {},
            onUsernameChanged = {},
            onSubmitClicked = {}
        )
    }
}
